import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Quote, Heart, MessageSquare, Shield, Scale, Users, UserCheck, Ear, HandHeart, FileText, Handshake, Star, HeartHandshake, Search, Sparkles } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const WomanPersonalInjuryLawyerPage = () => {
  const faqData = [
    {
      question: "Why do some clients specifically seek a woman personal injury lawyer?",
      answer: "Many clients mention they value clear communication, patience during difficult situations, and feeling heard and understood. Some feel more comfortable discussing sensitive injuries or personal challenges with a woman attorney. Every client has their own reasons, and we respect that."
    },
    {
      question: "What is Amy Brown's approach to personal injury cases?",
      answer: "Amy focuses on listening carefully to client concerns, explaining legal options clearly, and helping clients make informed decisions. Clients work directly with Amy and receive regular communication about their case throughout the process."
    },
    {
      question: "How does understanding the insurance process help my case?",
      answer: "Amy has experience working on legal matters involving insurance claims and understands how insurance companies evaluate cases. This knowledge helps build stronger claims and navigate the process more effectively on behalf of injured clients."
    },
    {
      question: "Will I work directly with Amy Brown on my case?",
      answer: "Yes. Clients work directly with Amy throughout their case. You won't be handed off to a paralegal or assistant for important decisions. Amy believes in building a direct relationship with each client."
    },
    {
      question: "Do you serve clients looking for a female personal injury lawyer in Snohomish County?",
      answer: "Yes. Amy serves clients throughout Snohomish County, including Everett, Lynnwood, Marysville, and Mukilteo. Many clients mention they specifically searched for a female accident lawyer or woman attorney when looking for representation after an injury."
    }
  ];

  const serviceData = {
    name: "Woman Personal Injury Lawyer",
    description: "Thoughtful personal injury representation with clear communication and patient guidance for injured clients seeking a woman attorney.",
    category: "Personal Injury Law"
  };

  return (
    <div className="min-h-screen pt-20" data-testid="woman-pi-lawyer-page">
      <SEOHead
        title="Female Personal Injury Lawyer in Everett | Amy C. Brown Law"
        description="Some clients specifically look for a female personal injury lawyer after an accident. Learn how Amy C. Brown Law helps injured clients in Everett and Snohomish County navigate insurance claims and move forward."
        keywords="female personal injury lawyer, woman personal injury attorney, female accident lawyer, woman lawyer Everett, female attorney Snohomish County"
        canonicalUrl="https://amycbrownlaw.com/woman-personal-injury-lawyer"
        faqData={faqData}
        serviceData={serviceData}
        localArea={{ city: "Everett", county: "Snohomish", state: "WA" }}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <p className="text-primary font-medium tracking-wide uppercase text-sm mb-4">Thoughtful Representation</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-8 leading-tight max-w-4xl">
            Working With a Woman Personal Injury Lawyer
          </h1>
          <p className="text-xl md:text-2xl text-slate-600 leading-relaxed max-w-2xl">
            Clear communication. Patient guidance. An attorney who listens.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-3">
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed mb-8">
                Some clients tell me they specifically wanted to work with a woman attorney after their accident. Every person has their own reasons for this preference, but many say they value clear communication, patience during difficult situations, and feeling heard and understood.
              </p>
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed">
                When people reach out after an accident, they're often feeling overwhelmed. My approach focuses on helping injured clients navigate the legal process with clarity and confidence.
              </p>
            </div>
            <div className="md:col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_66f84945-6f3f-4a21-9ecb-109e6dd76803/artifacts/zc7cbj2s_Amy%20office%202.jpeg"
                alt="Amy Brown reviewing case documents"
                className="rounded-xl shadow-2xl w-full object-cover aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Some Clients Choose a Woman Attorney */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Why Some Clients Choose a Woman Attorney
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-8">
            Many clients mention they intentionally searched for a woman attorney after their accident. The reasons vary from person to person, but some common themes come up in conversations:
          </p>
          
          <div className="grid md:grid-cols-2 gap-5 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Ear className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Careful Listening</h3>
              <p className="text-slate-600 text-sm">Wanting a lawyer who listens carefully before offering advice</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <HandHeart className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Comfortable Conversations</h3>
              <p className="text-slate-600 text-sm">Feeling more comfortable discussing injuries or personal challenges</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Clear Explanations</h3>
              <p className="text-slate-600 text-sm">Wanting clear explanations of the legal process without legal jargon</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Handshake className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Collaborative Approach</h3>
              <p className="text-slate-600 text-sm">Preferring a collaborative and thoughtful approach to decision-making</p>
            </div>
          </div>
          
          <p className="text-slate-600 italic text-center">
            These qualities aren't exclusive to women attorneys, of course. But I understand why some clients feel more comfortable seeking out a woman lawyer, and I take that trust seriously.
          </p>
        </div>
      </section>

      {/* What Clients Often Say */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-3 mb-8">
            <Star className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              What Clients Often Say
            </h2>
          </div>
          
          {/* Prominent Testimonial */}
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 md:p-10 relative mb-8">
            <div className="absolute top-6 left-6 w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
              <Quote className="w-8 h-8 text-primary" />
            </div>
            <div className="pt-12 md:pt-8 md:pl-20">
              <blockquote className="text-xl md:text-2xl text-secondary font-medium italic leading-relaxed mb-6">
                "After my accident I felt overwhelmed and didn't want to deal with a pushy lawyer. Amy took the time to listen, explain everything clearly, and guide me through the process. I felt supported every step of the way."
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/30 rounded-full flex items-center justify-center">
                  <span className="text-primary font-semibold">C</span>
                </div>
                <div>
                  <p className="text-slate-700 font-medium">Former Client</p>
                  <p className="text-slate-500 text-sm">Car Accident Case</p>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Many clients tell me they appreciate a thoughtful and communicative approach when dealing with insurance companies and the legal process. After an accident, the last thing most people want is to feel confused or pressured about decisions that affect their health and their future.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            I believe that feeling heard is just as important as getting answers. When clients know what's happening with their case and understand their options, they can make better decisions—and feel more confident about the path forward.
          </p>
        </div>
      </section>

      {/* Clear Communication Matters */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Clear Communication Matters
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            After an accident, most people are dealing with a lot at once:
          </p>
          
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Medical treatment and recovery</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Phone calls from insurance adjusters</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Financial concerns from missed work</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Uncertainty about what happens next</span>
            </li>
          </ul>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Clients often tell me they appreciate working with a lawyer who explains the process in plain language, answers questions patiently, and keeps them informed about their case. That's a central part of how I practice.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            Whether you've been hurt in a <Link to="/car-crashes" className="text-primary hover:underline">car accident</Link>, a <Link to="/slip-and-falls" className="text-primary hover:underline">slip and fall</Link>, or another type of incident, you deserve to understand what's happening with your case.
          </p>
        </div>
      </section>

      {/* Direct Communication With Your Attorney */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <UserCheck className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Direct Communication With Your Attorney
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            When you work with me, you work directly with the attorney handling your case—not a paralegal, not an assistant, and not a case manager. I believe personal communication is an important part of helping injured clients navigate a difficult situation.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            This direct relationship allows you to:
          </p>
          
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Ask questions and get answers from someone who knows your case</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Receive clear explanations about what's happening and why</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Stay informed about developments without chasing down updates</span>
            </li>
          </ul>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            Many clients tell me this approach helps reduce the stress of dealing with an injury claim. When you're already managing medical appointments, insurance calls, and daily life, the last thing you need is difficulty reaching your own attorney.
          </p>
        </div>
      </section>

      {/* Amy's Approach */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Scale className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              My Approach to Personal Injury Representation
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Every case is different, but my approach stays consistent:
          </p>
          
          <div className="grid gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-5">
              <h3 className="font-semibold text-secondary mb-2">Listening First</h3>
              <p className="text-slate-600">Before I can help, I need to understand your situation. That means listening carefully to your concerns, not rushing through an intake checklist.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5">
              <h3 className="font-semibold text-secondary mb-2">Explaining Clearly</h3>
              <p className="text-slate-600">Legal processes can be confusing. I take time to explain your options in plain English so you can make informed decisions.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5">
              <h3 className="font-semibold text-secondary mb-2">Building Thoughtfully</h3>
              <p className="text-slate-600">I focus on building strong cases rather than rushing toward quick settlements that may not reflect your full damages.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5">
              <h3 className="font-semibold text-secondary mb-2">Working Directly With You</h3>
              <p className="text-slate-600">You work directly with me throughout your case. You won't be handed off to someone else for important conversations.</p>
            </div>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            I serve clients throughout Washington State, with a focus on <Link to="/everett-personal-injury-lawyer" className="text-primary hover:underline">Everett and Snohomish County</Link>. Whether we meet in person or talk by phone, you'll receive the same level of attention and care.
          </p>
        </div>
      </section>

      {/* Understanding the Insurance Process */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Understanding the Insurance Process
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Earlier in my career, I worked on legal matters involving insurance claims. That experience gave me insight into how insurance companies evaluate cases, what factors they consider when making settlement offers, and the strategies they use to minimize payouts.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            Understanding this process from the inside helps me navigate claims more effectively on behalf of my clients. I know what documentation matters, how to present your damages clearly, and when an offer doesn't reflect the true value of your case.
          </p>
        </div>
      </section>

      {/* Comfort During Difficult Conversations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Heart className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Comfort During Difficult Conversations
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Injury cases sometimes involve sensitive topics. You may need to discuss:
          </p>
          
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Physical injuries and their impact on daily life</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Emotional stress or anxiety following an accident</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Ongoing medical treatment and its challenges</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Financial pressure from bills and lost income</span>
            </li>
          </ul>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            Some clients say they feel more comfortable discussing these challenges with a woman attorney who approaches the conversation with patience and understanding. I take that responsibility seriously and create space for clients to share what they're going through without feeling judged or rushed.
          </p>
        </div>
      </section>

      {/* Supporting Clients After Serious Injuries */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <HeartHandshake className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Supporting Clients After Serious Injuries
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Accidents often leave people dealing with more than just physical injuries. There's the stress of medical care, pressure from insurance companies, lost income from missed work, and uncertainty about what the future holds.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            My goal is to help clients navigate these challenges with thoughtful guidance and clear communication. You shouldn't have to figure out the legal process on your own while also trying to recover from your injuries.
          </p>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Whether You're Looking for a Woman Attorney or Simply Want Thoughtful Guidance
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Whatever brought you to this page, I appreciate you taking the time to learn about my approach. Whether you specifically wanted to work with a woman personal injury lawyer or you're simply looking for an attorney who will listen, explain things clearly, and guide you through a difficult time—I welcome the opportunity to speak with you about your situation.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            If you have questions about your situation or would like to discuss your case, you're welcome to <Link to="/contact" className="text-primary hover:underline">contact the office</Link> to schedule a conversation. There's no pressure and no obligation—just an opportunity to talk through what happened and explore your options.
          </p>
        </div>
      </section>

      {/* Looking for a Female Personal Injury Lawyer - SEO Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Search className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Looking for a Female Personal Injury Lawyer
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Some people searching for a female personal injury lawyer in Everett simply want an attorney who takes the time to listen and explain the process clearly. After an accident, many clients tell me they were looking for a woman personal injury attorney because they wanted someone who would approach their case thoughtfully rather than rushing toward a quick settlement.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Clients from across Snohomish County—including Everett, Lynnwood, Marysville, and Mukilteo—sometimes mention they specifically searched for a female accident lawyer because they valued:
          </p>
          
          <ul className="space-y-3 mb-6 ml-4">
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">A lawyer who listens carefully before offering advice</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Clear explanations of the legal process in plain English</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">A thoughtful, collaborative approach to resolving their case</span>
            </li>
          </ul>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Whether you've been injured in a <Link to="/car-crashes" className="text-primary hover:underline">car accident</Link> or another type of incident, I welcome clients who are looking for this type of representation. As a <Link to="/everett-personal-injury-lawyer" className="text-primary hover:underline">personal injury lawyer serving Everett</Link> and the surrounding communities, I'm committed to helping injured clients navigate the insurance process and pursue fair compensation.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            If you're looking for a female personal injury lawyer who will take the time to understand your situation and guide you through the process, I invite you to <Link to="/contact" className="text-primary hover:underline">reach out for a conversation</Link>. There's no pressure—just an opportunity to talk through what happened and explore your options.
          </p>
        </div>
      </section>

      {/* Soft CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Talk?
          </h2>
          <p className="text-slate-200 mb-8 text-lg">
            If you have questions about your situation or would like to discuss your case, you're welcome to reach out. I'm happy to listen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+14256751224"
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-medium transition-all"
              data-testid="woman-lawyer-call-btn"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a
              href="sms:+14256751224"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-full font-medium transition-all"
              data-testid="woman-lawyer-text-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>

      {/* Wikipedia Entity Links for E-E-A-T */}
      <section className="py-8 bg-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <p className="text-sm text-slate-500 text-center">
            Related legal concepts:{' '}
            <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Personal Injury</a>
            {' · '}
            <a href="https://en.wikipedia.org/wiki/Attorney%E2%80%93client_privilege" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Attorney-Client Privilege</a>
            {' · '}
            <a href="https://en.wikipedia.org/wiki/Contingent_fee" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Contingency Fee</a>
            {' · '}
            <a href="https://en.wikipedia.org/wiki/Insurance_bad_faith" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Insurance Claims</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default WomanPersonalInjuryLawyerPage;
